import { Link } from '@tanstack/react-router'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@trellis/roots/sheet'
import { Skeleton } from '@trellis/roots/skeleton'
import { StatusBadge } from '@trellis/roots/status'
import { Typography } from '@trellis/roots/typography'
import { EXPENSE_CATEGORY_META, TAX_TYPE_LABELS } from '@trellis/shared'
import type { ExpenseResponse } from '@trellis/shared'
import { format, parseISO } from 'date-fns'
import { ExternalLink, FileText } from 'lucide-react'
import type { ReactNode } from 'react'

import { useExpenseReceiptQuery } from '@/hooks'
import { formatBytes, formatCurrency } from '@/lib/format'

import { CLAIMED_BADGE_LABELS, CLAIMED_BADGE_STYLES } from '../constants'

const IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp']

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-0.5">
      <Typography variant="label" color="muted">
        {label}
      </Typography>
      <div className="text-sm">{children}</div>
    </div>
  )
}

function ReceiptPreview({
  expenseId,
  hasReceipt,
  mimeType,
  filename,
  sizeBytes,
}: {
  expenseId: string
  hasReceipt: boolean
  mimeType: string | null
  filename: string | null
  sizeBytes: number | null
}) {
  const receiptQuery = useExpenseReceiptQuery(expenseId, hasReceipt)
  const receiptUrl =
    receiptQuery.data?.data?.receiptUrls?.sm ??
    receiptQuery.data?.data?.receiptUrls?.xl ??
    null

  if (!hasReceipt) {
    return <span className="text-muted-foreground">No receipt</span>
  }

  if (receiptQuery.isLoading || !receiptUrl) {
    return <Skeleton className="h-40 w-full rounded-lg" />
  }

  if (mimeType && IMAGE_MIME.includes(mimeType)) {
    return (
      <img
        src={receiptUrl}
        alt="Receipt"
        className="max-h-64 w-full rounded-md border object-contain"
      />
    )
  }

  if (mimeType === 'application/pdf') {
    return (
      <iframe
        src={receiptUrl}
        className="h-64 w-full rounded-lg border-0"
        title="Receipt PDF"
      />
    )
  }

  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border p-4 text-center">
      <FileText className="text-muted-foreground size-8" />
      <Typography variant="body-sm" color="muted">
        {filename ?? 'Receipt'}
        {sizeBytes !== null && ` (${formatBytes(sizeBytes)})`}
      </Typography>
      <a
        href={receiptUrl}
        download={filename ?? undefined}
        className="text-primary text-sm underline underline-offset-2"
      >
        Download receipt
      </a>
    </div>
  )
}

export function ExpenseSummarySheet({
  expense,
  onOpenChange,
}: {
  expense: ExpenseResponse | null
  onOpenChange: (isOpen: boolean) => void
}) {
  const category = expense?.category ? EXPENSE_CATEGORY_META[expense.category] : null

  const claimableDisplay =
    expense?.claimableAmountType === 'PERCENTAGE'
      ? `${expense.claimableAmount}%${
          expense.totalCost > 0
            ? ` (${formatCurrency((expense.claimableAmount / 100) * expense.totalCost)})`
            : ''
        }`
      : formatCurrency(expense?.claimableAmount ?? 0)

  const plainDescription = expense?.description
    ? expense.description.replace(/<[^>]+>/g, '').trim()
    : null

  return (
    <Sheet open={expense !== null} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[min(90vw,520px)] max-w-none overflow-y-auto sm:max-w-none"
      >
        <SheetHeader>
          <SheetTitle>
            {expense?.title ??
              (expense?.type === 'REIMBURSEMENT'
                ? 'Client Reimbursement'
                : 'Business Expense')}
          </SheetTitle>
          <SheetDescription>
            {[
              expense?.type === 'REIMBURSEMENT'
                ? 'Client Reimbursement'
                : 'Business Expense',
              expense?.expenseNumber,
            ]
              .filter(Boolean)
              .join(' · ')}
          </SheetDescription>
        </SheetHeader>

        {expense && (
          <div className="mt-6 space-y-5 p-4">
            <SummaryRow label="Date">
              {format(parseISO(expense.expenseDate), 'PPP')}
            </SummaryRow>

            {expense.type === 'BUSINESS' && category && (
              <SummaryRow label="Category">
                {category.label}{' '}
                <span className="text-muted-foreground">(Code {category.code})</span>
              </SummaryRow>
            )}

            {expense.type === 'BUSINESS' && expense.lineItemTitle && (
              <SummaryRow label="Line Item">{expense.lineItemTitle}</SummaryRow>
            )}

            {expense.type === 'BUSINESS' && (
              <SummaryRow label="Status">
                {expense.claimed ? (
                  <StatusBadge
                    status="CLAIMED"
                    styles={CLAIMED_BADGE_STYLES}
                    labels={CLAIMED_BADGE_LABELS}
                  />
                ) : (
                  <span className="text-muted-foreground text-sm">Not claimed</span>
                )}
              </SummaryRow>
            )}

            {expense.type === 'REIMBURSEMENT' && expense.clientName && (
              <SummaryRow label="Client">{expense.clientName}</SummaryRow>
            )}

            {expense.type === 'REIMBURSEMENT' &&
              expense.invoiceLineItemId &&
              expense.invoiceId && (
                <SummaryRow label={expense.billed ? 'Billed to' : 'On invoice'}>
                  <Link
                    to="/invoices/$id"
                    params={{ id: expense.invoiceId }}
                    className="text-primary inline-flex items-center gap-1 text-sm underline underline-offset-2"
                  >
                    View invoice
                    <ExternalLink className="size-3.5" />
                  </Link>
                  {!expense.billed && (
                    <span className="text-muted-foreground ml-2 text-xs">
                      (pending — invoice not yet sent)
                    </span>
                  )}
                </SummaryRow>
              )}

            <div className="rounded-lg border p-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total cost</span>
                  <span className="font-medium tabular-nums">
                    {formatCurrency(expense.totalCost)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tax included</span>
                  <span>{expense.taxIncluded ? 'Yes' : 'No'}</span>
                </div>
                {expense.taxType !== 'NONE' && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Tax ({TAX_TYPE_LABELS[expense.taxType]}{' '}
                      {(expense.taxRate * 100).toFixed(0)}%)
                    </span>
                    <span className="tabular-nums">
                      {formatCurrency(expense.taxAmount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2">
                  <span className="text-muted-foreground">Claimable</span>
                  <span className="font-medium tabular-nums">{claimableDisplay}</span>
                </div>
              </div>
            </div>

            {plainDescription && (
              <SummaryRow label="Notes">
                <p className="leading-relaxed">{plainDescription}</p>
              </SummaryRow>
            )}

            <SummaryRow label="Receipt">
              <div className="mt-1">
                <ReceiptPreview
                  expenseId={expense.id}
                  hasReceipt={expense.hasReceipt}
                  mimeType={expense.receiptMimeType}
                  filename={expense.receiptFilename}
                  sizeBytes={expense.receiptSizeBytes}
                />
              </div>
            </SummaryRow>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
